import { Button, Flex, ScrollArea, Text } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { memo, useEffect, useState } from "react";
import Select, { ISelect } from "src/components/Select";
import { IJsonArr } from "src/utils/utils";
import { object, string } from "yup";

export type IMappingOption = ISelect & Record<string, string>;

export interface IDereferenceField {
  csvColumnHeader: string;
  fieldName: string;
  required: boolean;
  options: IMappingOption[]; // passed all keys/values where matching needs to be done
}

interface IRelationMappingTool {
  fields: IDereferenceField[];
  onSubmit: () => void;
  csvData: IJsonArr;
}

const findMatchingOption = (options: IMappingOption[], value: string) => {
  const similarityThreshold = 0.7; // Adjust the threshold as needed
  const calculateSimilarity = (str1: string, str2: string): number => {
    // Using Jaccard similarity
    const set1 = new Set(str1);
    const set2 = new Set(str2);

    const intersection = new Set([...set1].filter((x) => set2.has(x)));
    const union = new Set([...set1, ...set2]);
    const similarity = intersection.size / union.size;
    return similarity;
  };
  const matchingOption = options.find((option) =>
    Object.values(option).some((propertyValue) => {
      const lowerCaseValue = value.toLowerCase();
      const lowerCasePropertyValue = propertyValue.toLowerCase();

      // Check for exact match or (similarity > threshold)
      return (
        lowerCasePropertyValue === lowerCaseValue ||
        calculateSimilarity(lowerCasePropertyValue, lowerCaseValue) >
          similarityThreshold
      );
    })
  );

  return matchingOption;
};

const RelationMappingTool = ({
  fields,
  onSubmit,
  csvData,
}: IRelationMappingTool) => {
  const [csvCopy, setCsvCopy] = useState<IJsonArr>([]);

  useEffect(() => {
    if (!csvData.length) return;
    // Function to filter unique rows based on specific fieldNames
    const filterUniqueRows = () => {
      const uniqueRows: Set<string> = new Set();
      return csvData.filter((row) => {
        const uniqueKey = fields
          .map((field) => row[field.fieldName]?.trim())
          .join("_");
        if (!uniqueRows.has(uniqueKey) && uniqueKey && uniqueKey !== "_") {
          uniqueRows.add(uniqueKey);
          return true;
        }
        return false;
      });
    };

    // Use all fieldNames for filtering unique rows
    setCsvCopy(filterUniqueRows());
  }, [csvData, fields]);

  useEffect(() => {
    if (!csvCopy.length) return;
    const initialValues: Record<string, string> = fields.reduce(
      (acc, field) => {
        csvCopy.forEach((row) => {
          const optionMatch = findMatchingOption(
            field.options,
            row[field.fieldName]
          );
          const optionValue = optionMatch?.value || "";
          acc[`${field.fieldName}_${row[field.fieldName]}`] = optionValue;
        });

        return acc;
      },
      {} as Record<string, string>
    );
    form.setValues(initialValues);
    form.resetDirty(initialValues);
  }, [csvCopy]);

  const validationSchema = object().shape(
    fields.reduce((acc: any, field) => {
      csvCopy.forEach((row) => {
        const fieldValue = row[field.fieldName]?.trim();
        const fieldNameWithIndex = `${field.fieldName}_${fieldValue}`;
        // Check if the fieldValue is not empty before adding validation rules
        if (fieldValue !== "") {
          acc[fieldNameWithIndex] = field.required
            ? string().required("Required")
            : string();
        }
      });
      return acc;
    }, {})
  );

  const form = useForm({
    initialValues: {},
    validate: yupResolver(validationSchema),
  });

  const handleSubmit = (values: any) => {
    // Update csv Object
    Object.keys(values).reduce((acc, key) => {
      const [fieldName, rowIndex] = key.split("_");
      if (rowIndex !== undefined) {
        //Check with trim
        const matchingRows = acc.filter((row) => {
          const rowValue = row[fieldName]?.trim();
          return rowValue === rowIndex.trim();
        });
        matchingRows.forEach((matchingRow) => {
          matchingRow[fieldName] = values[key];
        });
      }
      return acc;
    }, csvData);
    onSubmit();
  };

  return (
    <form
      onSubmit={form.onSubmit((values) => {
        handleSubmit(values);
      })}
    >
      <Flex direction={"column"} align={"center"} justify={"center"} gap={"lg"}>
        {fields
          .filter((field) =>
            csvCopy.some((row) => row[field.fieldName]?.trim() !== "")
          )
          .map((field) => (
            <Flex
              direction={"column"}
              gap={"xs"}
              align={"center"}
              justify={"center"}
              key={field.fieldName}
            >
              <Text fw={"bold"} size={"lg"} underline>
                {field.csvColumnHeader}
              </Text>
              {csvCopy.map((row, rowIndex) => {
                if (!row[field.fieldName]) return;
                return (
                  <Flex
                    key={rowIndex}
                    justify={"center"}
                    align={"center"}
                    gap={"md"}
                  >
                    <Text sx={{ width: 200 }}>{row[field.fieldName]}</Text>
                    <Select
                      mt={0}
                      data={field.options}
                      placeholder={field.csvColumnHeader}
                      required
                      {...form.getInputProps(
                        `${field.fieldName}_${row[field.fieldName]}`
                      )}
                    />
                  </Flex>
                );
              })}
            </Flex>
          ))}

        <Button w={220} type="submit">
          Submit
        </Button>
      </Flex>
    </form>
  );
};

export default memo(RelationMappingTool);
